/**
 * Requêtes des communications au client — chunk 9.AD.1.
 *
 * Ce module ne construit aucune URL de formulaire et n'a jamais accès au token :
 * le serveur met en file, le worker dérive le lien au moment du rendu. Côté client,
 * on ne manipule que des identifiants.
 */

import type { ClientCommunication } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

/** GET /v1/client-communications?project_id= — la trace du dossier. */
export function useClientCommunications(projectId: string | undefined) {
  return useQuery({
    queryKey: ["client-communications", "list", projectId],
    queryFn: () =>
      api.get<{ items: ClientCommunication[]; next_cursor: string | null }>(
        `/v1/client-communications?project_id=${encodeURIComponent(projectId!)}&limit=50`,
      ),
    enabled: !!projectId,
    placeholderData: (prev) => prev,
  });
}

/**
 * Invalide la trace ET le verdict.
 *
 * Demander une information fait passer le manquant `open → in_progress` côté
 * serveur : afficher la nouvelle communication sans rafraîchir l'état du dossier
 * montrerait deux vérités sur le même écran.
 */
function useInvalidate(projectId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["client-communications", "list", projectId] });
    void qc.invalidateQueries({ queryKey: ["projects", projectId, "completeness"] });
    void qc.invalidateQueries({ queryKey: ["missing-information"] });
    void qc.invalidateQueries({ queryKey: ["intake-links"] });
  };
}

export type InfoRequestInput = {
  missing_information_id: string;
  contact_id: string;
  note?: string;
};

/** POST /v1/client-communications/info-request */
export function useRequestInfoFromClient(projectId: string | undefined) {
  const invalider = useInvalidate(projectId);
  return useMutation({
    mutationFn: (input: InfoRequestInput) =>
      api.post<{ id: string; queued: boolean }>("/v1/client-communications/info-request", input),
    onSuccess: invalider,
  });
}

export type FormInviteInput = {
  census_id?: string;
  project_id: string;
  contact_id: string;
  form_template_version_id?: string;
  expected_requirement_id?: string;
  intake_link_id?: string;
  note?: string;
};

/** POST /v1/client-communications/form-invite */
export function useSendFormToClient(projectId: string | undefined) {
  const invalider = useInvalidate(projectId);
  return useMutation({
    mutationFn: (input: FormInviteInput) =>
      api.post<{ id: string; intake_link_id: string; queued: boolean }>(
        "/v1/client-communications/form-invite",
        input,
      ),
    onSuccess: invalider,
  });
}
